import type { ReactNode } from "react";
import { Suspense, useEffect } from "react";

import RouteFallback from "@/components/RouteFallback/RouteFallback";
import { useCapabilityStore } from "@/stores/CapabilityStore";
import { useOrganisationStore } from "@/stores/OrganisationStore";

import AuthNavActions from "../components/AuthNavActions/AuthNavActions";
import LanguageSwitcher from "../components/LanguageSwitcher/LanguageSwitcher";
import AppNavbar from "../components/Navbar/AppNavbar";
import OrgSwitcher from "../components/OrgSwitcher/OrgSwitcher";
import SeoHead from "../components/SeoHead/SeoHead";
import useKeyboardScrollReset from "../hooks/useKeyboardScrollReset";
import useNavItems from "../hooks/useNavItems";

interface AppShellProps {
  children: ReactNode;
  showLangSwitcher?: boolean;
}

function AppShell({ children, showLangSwitcher = false }: AppShellProps) {
  const navItems = useNavItems();
  const { organisation } = useOrganisationStore();
  const loadCapabilities = useCapabilityStore((state) => state.load);
  useKeyboardScrollReset();

  // Loaded once here, for the whole authenticated app: several screens mark a
  // reserved action with a badge, and none of them should fetch on its own.
  useEffect(() => {
    if (organisation?.org_id) {
      void loadCapabilities(organisation.org_id);
    }
  }, [organisation?.org_id, loadCapabilities]);

  return (
    <div className="App">
      <SeoHead />
      <AppNavbar
        navItems={navItems}
        beforeChildren={<OrgSwitcher />}
        mobileBeforeChildren={
          showLangSwitcher ? <LanguageSwitcher mobile /> : undefined
        }
        mobileChildren={<AuthNavActions />}
      />
      <main>
        <Suspense fallback={<RouteFallback />}>{children}</Suspense>
      </main>
    </div>
  );
}

export default AppShell;
